'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

function useIntersectionObserver(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {setIsVisible(true);observer.unobserve(el);}},
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Our Wines', href: '/wines' },
  { label: 'Winemaking', href: '/winemaking' },
  { label: 'Team', href: '/team' },
  { label: 'Contact', href: '/contact' }];


  return (
    <>
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center transition-all duration-500"
      style={{ background: 'rgba(10,7,5,0.97)', backdropFilter: 'blur(20px)', opacity: menuOpen ? 1 : 0, pointerEvents: menuOpen ? 'auto' : 'none' }}>
        <button className="absolute top-6 right-6 p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
        </button>
        <nav className="flex flex-col gap-8 text-center">
          {navLinks.map((link) =>
          <Link key={link.href + link.label} href={link.href}
          className="font-display text-3xl font-medium italic text-[#F5EDD6] hover:text-[#C9A84C] transition-colors duration-300"
          onClick={() => setMenuOpen(false)}>{link.label}</Link>
          )}
        </nav>
        <div className="mt-10 text-[#9A8A72] text-xs font-sans uppercase tracking-[0.3em]">Est. 1850 · Pauillac, France</div>
      </div>

      <nav className="fixed z-50 top-0 inset-x-0 px-6 py-3 flex items-center justify-between transition-all duration-500"
      style={{ background: scrolled ? 'rgba(10,7,5,0.85)' : 'transparent', backdropFilter: scrolled ? 'blur(12px)' : 'none', borderBottom: scrolled ? '1px solid rgba(46,26,16,0.6)' : 'none' }}>
        <Link href="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
              <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
              <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
            </svg>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-base font-semibold tracking-[0.2em] text-[#F5EDD6] uppercase">Baron Castle</span>
            <span className="text-[9px] font-sans tracking-[0.35em] text-[#C9A84C] uppercase mt-0.5">Château Baron · Est. 1850</span>
          </div>
        </Link>
        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) =>
          <Link key={link.href + link.label} href={link.href}
          className="text-sm font-medium text-[#9A8A72] hover:text-[#F5EDD6] transition-colors duration-300 relative group">
              {link.label}
              <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#C9A84C] group-hover:w-full transition-all duration-300" />
            </Link>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link href="/contact" className="hidden md:inline-flex items-center gap-2 px-5 py-2 border border-[#C9A84C]/50 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A84C] hover:text-[#0A0705] transition-all duration-300">
            Visit Estate
          </Link>
          <button className="lg:hidden p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" /></svg>
          </button>
        </div>
      </nav>
    </>);

}

function Footer() {
  return (
    <footer className="border-t border-[#2E1A10] bg-[#0A0705] pt-16 pb-8">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-12 mb-12">
          <div className="max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
                <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
                <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
              </svg>
              <div className="flex flex-col leading-none">
                <span className="font-display text-lg font-semibold tracking-[0.2em] text-[#F5EDD6] uppercase">Baron Castle</span>
                <span className="text-[9px] font-sans tracking-[0.35em] text-[#C9A84C] uppercase mt-0.5">Château Baron · Est. 1850</span>
              </div>
            </div>
            <p className="text-[#9A8A72] text-sm leading-relaxed italic font-display">A historic Bordeaux château crafting wines of extraordinary depth and elegance since 1850.</p>
            <p className="text-[#9A8A72] text-xs mt-4 font-sans">Route des Châteaux, Saint Lambert<br />33250 Pauillac, Gironde, France</p>
          </div>
          <div className="flex flex-wrap gap-x-16 gap-y-8">
            <div className="flex flex-col gap-3">
              <span className="text-xs font-sans uppercase tracking-widest text-[#C9A84C] mb-1">Estate</span>
              <Link href="/" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Home</Link>
              <Link href="/about" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Our Heritage</Link>
              <Link href="/winemaking" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Winemaking</Link>
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-xs font-sans uppercase tracking-widest text-[#C9A84C] mb-1">Discover</span>
              <Link href="/wines" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Our Wines</Link>
              <Link href="/team" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Team & Investors</Link>
              <Link href="/contact" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Visit Us</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-[#2E1A10] pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="text-xs text-[#9A8A72] font-sans">© 2025 圣兰博之围 · Château Baron, Pauillac AOC · All rights reserved</span>
        </div>
      </div>
    </footer>);

}

interface ProcessStep {
  number: string;
  title: string;
  subtitle: string;
  body: string;
  details: string;
  image: string;
  alt: string;
}

const PROCESS_STEPS: ProcessStep[] = [
{
  number: '01',
  title: 'Terroir & Viticulture',
  subtitle: 'The Foundation',
  body: 'Everything begins in the soil. Our 45 hectares of Günzian gravel force vine roots 8–12 metres deep in search of water and nutrients, producing grapes of extraordinary concentration. Biodynamic practices, adopted in 2008, restore and maintain the natural microbial life of the soil. We observe lunar cycles for pruning, harvesting, and bottling.',
  details: 'Vine density: 8,500 plants/ha · Average vine age: 42 years · Rootstock: 101-14 · Pruning: Guyot double',
  image: 'https://images.unsplash.com/photo-1452443191217-08a4f1f238e6?w=900&q=80',
  alt: 'Close-up of Bordeaux Cabernet Sauvignon grape clusters on vine with gravel soil visible below'
},
{
  number: '02',
  title: 'Harvest',
  subtitle: 'The Moment of Truth',
  body: 'Harvest at 圣兰博之围 is entirely by hand — there are no machines in our vineyards. We employ 120 seasonal harvesters who work in teams of eight, moving through each parcel with precision. A double-sorting process — first in the vineyard, then on the sorting table in the chai — ensures only perfect fruit enters our vats.',
  details: 'Harvest period: Late September – Early October · Sorting: Double triage, optical sorting table · Yield: 35–42 hl/ha',
  image: 'https://images.unsplash.com/photo-1549376557-174eb285b354?w=900&q=80',
  alt: 'Hands carefully harvesting red wine grapes from vine in warm afternoon light in Bordeaux vineyard'
},
{
  number: '03',
  title: 'Fermentation',
  subtitle: 'The Transformation',
  body: 'Grapes are fermented by parcel in our combination of temperature-controlled stainless steel vats and traditional wooden fermenters. Indigenous yeasts, native to the 圣兰博之围 chai, initiate fermentation naturally. Gentle pump-overs and pigeage extract colour, tannin, and aromatic complexity without aggression. Total maceration: 25–35 days.',
  details: 'Fermenters: 24 stainless steel (150–200 hl) + 6 oak vats · Temperature: 28–30°C peak · Maceration: 25–35 days',
  image: 'https://images.unsplash.com/photo-1733664094821-f215b68cfada?w=900&q=80',
  alt: 'Stainless steel wine fermentation tanks in modern winery with dim blue-tinted light'
},
{
  number: '04',
  title: 'Élevage in Oak',
  subtitle: 'The Long Patience',
  body: 'After the assemblage in January, 圣兰博之围 Grand Vin spends 22 months in French oak barriques of 225 litres, 35% new. Barrels are sourced exclusively from the Tronçais and Allier forests, chosen for their fine, tight grain. Monthly racking and traditional egg-white fining clarify the wine without filtration. The result is a wine of remarkable purity and depth.',
  details: 'Oak: French, 225L barriques · New oak: 35% Grand Vin, 20% Second Wine · Élevage: 22 months · Fining: Egg-white · Filtration: None',
  image: 'https://images.unsplash.com/photo-1657053488881-86657ee81726?w=900&q=80',
  alt: 'French oak wine barrels aging in stone château cellar with vaulted ceiling and dim lighting'
}];


const AGING_TABLE = [
{ wine: 'Grand Vin', oak: '22 months', newOak: '35% new', ready: '8–15 yrs', peak: '15–40 yrs', potential: '50+ yr' },
{ wine: 'Réserve', oak: '24 months', newOak: '50% new', ready: '10–18 yrs', peak: '18–45 yrs', potential: '50+ yr' },
{ wine: 'Second Wine', oak: '14 months', newOak: '20% new', ready: '3–6 yrs', peak: '6–15 yrs', potential: '20+ yr' },
{ wine: 'Blanc', oak: '10 months', newOak: '0% new', ready: '1–2 yrs', peak: '3–8 yrs', potential: '12+ yr' }];


function ProcessStep({ step, index }: {step: ProcessStep;index: number;}) {
  const { ref, isVisible } = useIntersectionObserver(0.1);
  const isEven = index % 2 === 0;
  return (
    <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 py-20 border-b border-[#2E1A10]"
    style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
      <div className={`flex flex-col justify-center ${isEven ? '' : 'lg:order-2'}`}>
        <div className="flex items-center gap-3 mb-6">
          <span className="font-sans text-xs tracking-[0.25em] text-[#C9A84C]">Step {step.number}</span>
          <span className="w-6 h-px bg-[#2E1A10]" />
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#9A8A72]">{step.subtitle}</span>
        </div>
        <h3 className="font-display font-medium text-[#F5EDD6] leading-tight mb-6" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)' }}>
          {step.title}
        </h3>
        <p className="text-[#9A8A72] leading-relaxed mb-6">{step.body}</p>
        <div className="border-l-2 border-[#C9A84C]/30 pl-4">
          <p className="text-[#9A8A72] text-xs leading-relaxed">{step.details}</p>
        </div>
      </div>
      <div className={`relative ${isEven ? '' : 'lg:order-1'}`}>
        <img src={step.image} alt={step.alt} className="w-full object-cover" style={{ aspectRatio: '4/3' }} />
        <div className="absolute top-4 left-4 w-10 h-10 border border-[#C9A84C]/40 flex items-center justify-center bg-[#0A0705]/80">
          <span className="font-display text-sm font-medium text-[#C9A84C]">{step.number}</span>
        </div>
      </div>
    </div>);

}

export default function WinemakingPage() {
  const { ref: cellarRef, isVisible: cellarVisible } = useIntersectionObserver(0.1);
  const { ref: agingRef, isVisible: agingVisible } = useIntersectionObserver(0.1);

  return (
    <main className="bg-[#0A0705] text-[#F5EDD6] overflow-x-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#C9A84C]" />
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C]">The Art & Science of Pauillac</span>
        </div>
        <h1 className="font-display font-medium text-[#F5EDD6] leading-none select-none" style={{ fontSize: 'clamp(3rem, 10vw, 10rem)' }}>
          The Craft of Winemaking
        </h1>
        <p className="font-display italic text-lg md:text-xl text-[#F5EDD6]/70 max-w-2xl leading-relaxed mt-6">
          At 圣兰博之围, winemaking is not a process — it is a philosophy. Every decision, from the moment the vine flowers to the day the bottle is sealed, is guided by the singular pursuit of excellence.
        </p>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-16"><div className="h-px bg-[#2E1A10]" /></div>

      {/* Process Steps */}
      <section className="px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 py-14">
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C]">01 — The Process</span>
          <span className="w-8 h-px bg-[#2E1A10]" />
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#9A8A72]">From Vine to Bottle</span>
        </div>
        {PROCESS_STEPS.map((step, i) => <ProcessStep key={step.number} step={step} index={i} />)}
      </section>

      {/* Grand Cave */}
      <section className="py-24 px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div ref={cellarRef} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
        style={{ opacity: cellarVisible ? 1 : 0, transform: cellarVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
          <div>
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C] block mb-6">02 — The Cellars</span>
            <h2 className="font-display font-medium text-[#F5EDD6] leading-tight mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
              The Grand Cave Baron
            </h2>
            <p className="text-[#9A8A72] leading-relaxed mb-6">
              Built in 1878 by the second generation of the Baron family, our stone cellar maintains a constant temperature of 12°C and humidity of 85% year-round — the perfect conditions for the slow, patient evolution of great Bordeaux.
            </p>
            <p className="text-[#9A8A72] leading-relaxed mb-10">
              The cellar houses 600 barriques simultaneously, arranged in three vaulted chambers. The oldest stones are original to 1878. Our cellar master, Élodie Marchais, oversees every barrel personally, tasting from each cask monthly to monitor development and determine the optimal bottling date.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {[
              { v: '600', l: 'Barriques Capacity' },
              { v: '12°C', l: 'Constant Temp.' },
              { v: '1878', l: 'Year Built' }].
              map((s) =>
              <div key={s.l} className="border-l-2 border-[#C9A84C]/30 pl-4">
                  <span className="font-display text-2xl font-medium text-[#C9A84C] block">{s.v}</span>
                  <span className="text-[10px] font-sans uppercase tracking-[0.15em] text-[#9A8A72]">{s.l}</span>
                </div>
              )}
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1657053488881-86657ee81726?w=900&q=80"
              alt="The Grand Cave Baron — vaulted stone cellar built in 1878 housing 600 French oak barriques"
              className="w-full object-cover"
              style={{ aspectRatio: '4/5' }} />
            
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-16"><div className="h-px bg-[#2E1A10]" /></div>

      {/* Aging Potential */}
      <section className="py-24 px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div ref={agingRef} style={{ opacity: agingVisible ? 1 : 0, transform: agingVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C] block mb-6">03 — Aging Potential</span>
          <h2 className="font-display font-medium text-[#F5EDD6] leading-tight mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
            Wines Built for Decades
          </h2>
          <p className="text-[#9A8A72] leading-relaxed max-w-2xl mb-6">
            The hallmark of great Pauillac is longevity. The deep gravel soils, high Cabernet Sauvignon content, and our meticulous élevage produce wines that reward patience with extraordinary complexity.
          </p>
          <p className="text-[#9A8A72] leading-relaxed max-w-2xl mb-10">
            Our 1961 Grand Vin, still in perfect condition after 64 years, stands as testament to what Pauillac terroir and careful winemaking can achieve. We cellar back-vintages of every wine produced, ensuring that future generations can experience the full arc of 圣兰博之围's evolution.
          </p>
          <blockquote className="font-display italic text-[#F5EDD6]/60 text-lg leading-relaxed border-l-2 border-[#C9A84C]/30 pl-6 mb-14 max-w-2xl">
            "We do not make wines for today. We make wines for your children."
            <footer className="mt-2 text-[10px] font-sans uppercase tracking-[0.2em] text-[#C9A84C] not-italic">— Élodie Marchais, Maître de Chai</footer>
          </blockquote>

          {/* Aging table */}
          <div className="overflow-x-auto">
            <table className="w-full border border-[#2E1A10] text-sm">
              <thead>
                <tr className="border-b border-[#2E1A10]">
                  {['Wine', 'Oak Élevage', 'New Oak', 'Ready to Drink', 'Peak Window', 'Aging Potential'].map((h) =>
                  <th key={h} className="text-left px-6 py-4 text-[10px] font-sans uppercase tracking-[0.2em] text-[#C9A84C]">{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {AGING_TABLE.map((row, i) =>
                <tr key={row.wine} className="border-b border-[#2E1A10] last:border-b-0" style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(46,26,16,0.15)' }}>
                    <td className="px-6 py-4 font-display font-medium text-[#F5EDD6]">{row.wine}</td>
                    <td className="px-6 py-4 text-[#9A8A72]">{row.oak}</td>
                    <td className="px-6 py-4 text-[#9A8A72]">{row.newOak}</td>
                    <td className="px-6 py-4 text-[#9A8A72]">{row.ready}</td>
                    <td className="px-6 py-4 text-[#9A8A72]">{row.peak}</td>
                    <td className="px-6 py-4 text-[#C9A84C] font-medium">{row.potential}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-10">
            <Link href="/wines" className="inline-flex items-center gap-3 px-8 py-3 border border-[#C9A84C]/50 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A84C] hover:text-[#0A0705] transition-all duration-300">
              Explore Our Wines
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>);

}